/* eslint-disable max-len */
import React, {useState, useEffect, useRef, useCallback} from 'react';
import {Editor, Transforms, Range, Element as SlateElement} from 'slate';
import {useSlate, ReactEditor} from 'slate-react';

import '../../styles/_selectionPopup.scss';
import {setLineSpacing} from './constants';
import ToolbarIcons from './ToolbarIcons';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SelectionPopupMenuProps {
  toggleMark: (format: string) => void;
  setFontSize: (size: number) => void;
  setFontFamily: (font: string) => void;
  toggleColor: (type: string, color: string) => void;
  setAlignment: (align: string) => void;
  toggleBlock: (format: string) => void;
  insertLink: () => void;
  insertComment: () => void;
  handleToggleList: (format: string) => void;
  toolbarSync: any;
  openColorPicker: (
    type: 'text' | 'highlight' | 'block-bg',
    anchorEl?: HTMLElement | null
  ) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

const SelectionPopupMenu: React.FC<SelectionPopupMenuProps> = ({
  toggleMark,
  setFontSize,
  setFontFamily,
  setAlignment,
  insertLink,
  insertComment,
  handleToggleList,
  toolbarSync,
  openColorPicker
}) => {
  const editor = useSlate();
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({top: 0, left: 0});
  const [lineSpacingOpen, setLineSpacingOpen] = useState(false);
  const [alignOpen, setAlignOpen] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);
  const lineSpacingRef = useRef<HTMLDivElement>(null);
  const alignRef = useRef<HTMLDivElement>(null);
  const closePopup = useCallback(() => {
    setVisible(false);
    setLineSpacingOpen(false);
    setAlignOpen(false);
  }, []);

  const FONT_FAMILIES = [
    'Inter',
    'Arial',
    'Times New Roman',
    'Courier New',
    'Georgia',
    'Verdana',
    'Trebuchet MS',
    'Calibri',
    'Montserrat'
  ];
  const LINE_SPACINGS = [1, 1.2, 1.5, 2];

  // ─── Popup position logic ─────────────────────────────────────────────────

  const updatePosition = useCallback(() => {
    const {selection} = editor;
    if (!selection) return;

    try {
      const domSel = window.getSelection();
      if (!domSel || domSel.rangeCount === 0) return;

      const range = domSel.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      const clientRect = range.getClientRects()[0];
      const anchorRect =
        rect && (rect.width !== 0 || rect.height !== 0) ? rect : clientRect;
      if (!anchorRect) return;

      const POPUP_WIDTH = 200;
      const POPUP_HEIGHT = 250; // approximate, used for viewport fit
      const VIEWPORT_GAP = 8;
      const SELECTION_GAP = 10;

      const centeredLeft =
        anchorRect.left + anchorRect.width / 2 - POPUP_WIDTH / 2;
      const left = Math.max(
        VIEWPORT_GAP,
        Math.min(centeredLeft, window.innerWidth - POPUP_WIDTH - VIEWPORT_GAP)
      );

      const roomAbove = anchorRect.top - VIEWPORT_GAP;
      const roomBelow = window.innerHeight - anchorRect.bottom - VIEWPORT_GAP;

      let top = anchorRect.bottom + SELECTION_GAP;
      if (roomBelow < POPUP_HEIGHT && roomAbove >= POPUP_HEIGHT) {
        top = anchorRect.top - POPUP_HEIGHT - SELECTION_GAP;
      } else if (roomBelow < POPUP_HEIGHT && roomAbove < POPUP_HEIGHT) {
        top = Math.max(
          VIEWPORT_GAP,
          Math.min(
            anchorRect.bottom + SELECTION_GAP,
            window.innerHeight - POPUP_HEIGHT - VIEWPORT_GAP
          )
        );
      }

      setPosition({top, left});
    } catch (e) {
      // ignore
    }
  }, [editor]);

  // ─── Show/hide logic ─────────────────────────────────────────────────────

  useEffect(() => {
    const handleMouseUp = () => {
      requestAnimationFrame(() => {
        const {selection} = editor;
        if (!selection) {
          setVisible(false);

          return;
        }
        if (!selection || Range.isCollapsed(selection)) {
          setVisible(false);

          return;
        }
        // Only show if cursor is in a non-void, non-structural block
        const [match] = Array.from(
          Editor.nodes(editor, {
            match: (n) =>
              !Editor.isEditor(n) &&
              SlateElement.isElement(n) &&
              ![
                'page',
                'header-wrapper',
                'footer-wrapper',
                'page-break',
                'image',
                'placeholder-image'
              ].includes(n.type),
            mode: 'lowest'
          })
        );
        if (!match) {
          setVisible(false);

          return;
        }
        updatePosition();
        setVisible(true);
      });
    };

    const editorPanel = document.querySelector('.editor-panel');
    if (editorPanel) {
      editorPanel.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      if (editorPanel) {
        editorPanel.removeEventListener('mouseup', handleMouseUp);
      }
    };
  }, [editor, updatePosition]);

  // Hide popup on scroll so stale floating menu does not remain onscreen
  useEffect(() => {
    if (!visible) return;

    const editorPanel = document.querySelector('.editor-panel');
    editorPanel?.addEventListener('scroll', closePopup, {passive: true});
    window.addEventListener('scroll', closePopup, {passive: true});

    return () => {
      editorPanel?.removeEventListener('scroll', closePopup);
      window.removeEventListener('scroll', closePopup);
    };
  }, [visible, closePopup]);

  // Hide popup when tab/window/page focus changes
  useEffect(() => {
    if (!visible) return;

    const handleVisibilityChange = () => {
      if (document.hidden) closePopup();
    };

    window.addEventListener('blur', closePopup);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', closePopup);
    window.addEventListener('popstate', closePopup);
    window.addEventListener('hashchange', closePopup);

    return () => {
      window.removeEventListener('blur', closePopup);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', closePopup);
      window.removeEventListener('popstate', closePopup);
      window.removeEventListener('hashchange', closePopup);
    };
  }, [visible, closePopup]);

  // Close popup on outside click
  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        closePopup();
      }
    };
    if (visible) {
      document.addEventListener('mousedown', handleMouseDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [visible, closePopup]);

  // Close sub-menus on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        lineSpacingRef.current &&
        !lineSpacingRef.current.contains(e.target as Node)
      ) {
        setLineSpacingOpen(false);
      }
      if (alignRef.current && !alignRef.current.contains(e.target as Node)) {
        setAlignOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // ─── Action helpers ───────────────────────────────────────────────────────

  const act = (fn: () => void) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    fn();
  };

  const deleteSelection = () => {
    const {selection} = editor;
    if (selection && !Range.isCollapsed(selection)) {
      Transforms.delete(editor);
    }
    setVisible(false);
  };

  const isMarkActive = (format: string) => !!toolbarSync?.[format];
  const currentAlign = toolbarSync?.align || 'left';
  const currentFontFamily = toolbarSync?.fontFamily || 'Inter';
  const currentFontSize = toolbarSync?.fontSize || 12;

  if (!visible) return null;

  return (
    <div
      ref={popupRef}
      className="spm-popup"
      style={{top: position.top, left: position.left}}
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {/* ── Row 1: Formatting Bar ──────────────────────────────────────── */}
      <div className="spm-formatting-bar">
        {/* Font Family */}
        <select
          className="spm-select"
          value={currentFontFamily}
          title="Font Family"
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
          onChange={(e) => {
            e.preventDefault();
            setFontFamily(e.target.value);
          }}
          style={{width: 88, fontSize: 12}}
        >
          {FONT_FAMILIES.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>

        {/* Font Size */}
        <div className="spm-fontsize-wrapper">
          <button
            className="spm-fontsize-btn"
            title="Decrease font size"
            onMouseDown={act(() => {
              setFontSize(Math.max(6, currentFontSize - 1));
            })}
          >
            −
          </button>
          <span className="spm-fontsize-display">{currentFontSize}</span>
          <button
            className="spm-fontsize-btn"
            title="Increase font size"
            onMouseDown={act(() => {
              setFontSize(currentFontSize + 1);
            })}
          >
            +
          </button>
        </div>

        {/* Grow / Shrink */}
        <button
          className="spm-btn"
          title="Grow font (A+)"
          onMouseDown={act(() => {
            setFontSize(currentFontSize + 1);
          })}
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
            <text
              y="18"
              fontSize="18"
              fontWeight="bold"
              fontFamily="sans-serif"
            >
              A
            </text>
            <text x="14" y="22" fontSize="10" fontFamily="sans-serif">
              +
            </text>
          </svg>
        </button>
        <button
          className="spm-btn"
          title="Shrink font (A-)"
          onMouseDown={act(() => {
            setFontSize(Math.max(6, currentFontSize - 1));
          })}
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
            <text
              y="18"
              fontSize="18"
              fontWeight="bold"
              fontFamily="sans-serif"
            >
              A
            </text>
            <text x="14" y="22" fontSize="10" fontFamily="sans-serif">
              -
            </text>
          </svg>
        </button>

        <div className="spm-divider-v" />

        {/* Clear Formatting */}
        <button
          className="spm-btn"
          title="Clear formatting"
          onMouseDown={act(() => {
            const {selection} = editor;
            if (!selection) return;
            [
              'bold',
              'italic',
              'underline',
              'strikethrough',
              'subscript',
              'superscript',
              'color',
              'highlight'
            ].forEach((f) => {
              Editor.removeMark(editor, f);
            });
            ReactEditor.focus(editor as ReactEditor);
          })}
        >
          <ToolbarIcons tool="clearFormatting" />
        </button>

        <div className="spm-divider-v" />

        {/* Bold */}
        <button
          className={`spm-btn ${isMarkActive('bold') ? 'active' : ''}`}
          title="Bold"
          onMouseDown={act(() => {
            toggleMark('bold');
          })}
        >
          <ToolbarIcons tool="bold" />
        </button>

        {/* Italic */}
        <button
          className={`spm-btn ${isMarkActive('italic') ? 'active' : ''}`}
          title="Italic"
          onMouseDown={act(() => {
            toggleMark('italic');
          })}
        >
          <ToolbarIcons tool="italic" />
        </button>

        {/* Underline */}
        <button
          className={`spm-btn ${isMarkActive('underline') ? 'active' : ''}`}
          title="Underline"
          onMouseDown={act(() => {
            toggleMark('underline');
          })}
        >
          <ToolbarIcons tool="underline" />
        </button>

        {/* Strikethrough */}
        <button
          className={`spm-btn ${isMarkActive('strikethrough') ? 'active' : ''}`}
          title="Strikethrough"
          onMouseDown={act(() => {
            toggleMark('strikethrough');
          })}
        >
          <ToolbarIcons tool="strikeThrough" />
        </button>

        {/* Subscript */}
        <button
          className={`spm-btn ${isMarkActive('subscript') ? 'active' : ''}`}
          title="Subscript"
          onMouseDown={act(() => {
            toggleMark('subscript');
          })}
        >
          <ToolbarIcons tool="subScript" />
        </button>
        <button
          className={`spm-btn ${isMarkActive('superscript') ? 'active' : ''}`}
          title="Superscript"
          onMouseDown={act(() => {
            toggleMark('superscript');
          })}
        >
          <ToolbarIcons tool="superScript" />
        </button>

        <div className="spm-divider-v" />

        <div className="spm-color-btn-wrap" title="Highlight color">
          <button
            className="spm-btn"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              openColorPicker('highlight', e.currentTarget);
            }}
          >
            <ToolbarIcons tool="highlight" />
          </button>
        </div>

        {/* Text color */}
        <div className="spm-color-btn-wrap" title="Text color">
          <button
            className="spm-btn"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              openColorPicker('text', e.currentTarget);
            }}
          >
            <ToolbarIcons tool="textColor" />
          </button>
        </div>

        <div className="spm-divider-v" />

        {/* Line Spacing */}
        <div ref={lineSpacingRef} style={{position: 'relative'}}>
          <button
            className={`spm-btn ${lineSpacingOpen ? 'active' : ''}`}
            title="Line Spacing"
            onMouseDown={act(() => {
              setLineSpacingOpen(!lineSpacingOpen);
            })}
          >
            <ToolbarIcons tool="lineHeight" />
          </button>
          {lineSpacingOpen && (
            <div className="spm-submenu">
              {LINE_SPACINGS.map((s) => (
                <button
                  key={s}
                  className="spm-submenu-item"
                  onMouseDown={act(() => {
                    setLineSpacing(
                      editor,
                      s,
                      editor.selection ? [editor.selection] : []
                    );
                    ReactEditor.focus(editor as ReactEditor);
                    setLineSpacingOpen(false);
                  })}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Text Align */}
        <div ref={alignRef} style={{position: 'relative'}}>
          <button
            className={`spm-btn ${alignOpen ? 'active' : ''}`}
            title={`Align: ${currentAlign}`}
            onMouseDown={act(() => {
              setAlignOpen(!alignOpen);
            })}
          >
            <ToolbarIcons tool="left" />
            <svg
              viewBox="0 0 24 24"
              width="8"
              height="8"
              fill="currentColor"
              style={{marginLeft: 1}}
            >
              <path d="M7 10l5 5 5-5z" />
            </svg>
          </button>
          {alignOpen && (
            <div
              className="spm-submenu"
              style={{flexDirection: 'row', padding: 4, gap: 2}}
            >
              {(['left', 'center', 'right', 'justify'] as const).map((a) => (
                <button
                  key={a}
                  className={`spm-btn ${currentAlign === a ? 'active' : ''}`}
                  title={`Align ${a}`}
                  onMouseDown={act(() => {
                    setAlignment(a);
                    setAlignOpen(false);
                  })}
                >
                  <ToolbarIcons tool={a} />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Divider ───────────────────────────────────────────────────────── */}
      <div className="spm-divider-h" />

      {/* ── Row 2: Menu List ─────────────────────────────────────────────── */}
      <div className="spm-menu-list">
        {/* Delete Selection */}
        <button
          className="spm-menu-item danger"
          onMouseDown={act(deleteSelection)}
        >
          <span className="spm-menu-icon">
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="#e53935"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14H6L5 6" />
              <path d="M10 11v6M14 11v6" />
              <path d="M9 6V4h6v2" />
            </svg>
          </span>
          <span>Delete Selection</span>
        </button>

        <div className="spm-divider-h" />

        {/* Bullets */}
        <button
          className="spm-menu-item"
          onMouseDown={act(() => {
            handleToggleList('bulleted-list');
            setVisible(false);
          })}
        >
          <ToolbarIcons tool="bulletList" />
          <span>Bullet list</span>
        </button>

        {/* Numbering */}
        <button
          className="spm-menu-item"
          onMouseDown={act(() => {
            handleToggleList('numbered-list');
            setVisible(false);
          })}
        >
          <ToolbarIcons tool="numberedList" />
          <span>Numbering list</span>
        </button>

        {/* Link */}
        <button
          className="spm-menu-item"
          onMouseDown={act(() => {
            insertLink();
            setVisible(false);
          })}
        >
          <ToolbarIcons tool="link" />
          <span>Link</span>
          <span className="spm-menu-shortcut">Ctrl+K</span>
        </button>

        {/* New Comment */}
        <button
          className="spm-menu-item"
          onMouseDown={act(() => {
            insertComment();
            closePopup();
            Transforms.deselect(editor);
          })}
        >
          <ToolbarIcons tool="comment" />
          <span>New Comment</span>
        </button>
      </div>
    </div>
  );
};

export default SelectionPopupMenu;
